import React from "react";
import { Heading } from "components/ui/Text";
import Link from "components/ui/Link";
import Icon from "components/ui/Icon";
import { ApiAdminUser } from "utils/api/admin/user";
import { ProductName } from "types/general";
import StatusTag from "../../components/StatusTag";
import PartnerTag from "../../components/PartnerTag";
import OnboardingStateWithProgress from "./BasicInfo/OnboardingStateWithProgress";
import * as Styled from "./styled";

interface Props {
  user: ApiAdminUser;
}

const BasicInfo: React.FC<Props> = ({ user }) => {
  const { partnerSlug, referralSlug } = user.userConfig;

  return (
    <Styled.SectionTile>
      <Heading.H3 tag="h1">{user.name}</Heading.H3>

      <Styled.Info>
        <Styled.InfoSection>
          <Styled.InfoItem label="Email" value={user.email} />
          <Styled.InfoItem
            label="Links"
            value={
              <>
                <Link
                  href={user.hubspotContactUrl}
                  target="_blank"
                  size="lg"
                  color="azure"
                >
                  Open in Hubspot <Icon name="externalLink" />
                </Link>
                {user.syncteraCustomerUrl && (
                  <Link
                    href={user.syncteraCustomerUrl}
                    target="_blank"
                    size="lg"
                    color="azure"
                  >
                    Open in Synctera <Icon name="externalLink" />
                  </Link>
                )}
              </>
            }
          />
          {user.potentialDuplicateUsers?.length > 0 && (
            <Styled.InfoItem
              label="Potential Duplicate Users"
              value={
                <div>
                  {user.potentialDuplicateUsers.map((duplicate) => (
                    <div
                      key={duplicate.shortId}
                      style={{ marginBottom: "8px" }}
                    >
                      <Link href={duplicate.url} size="lg" color="azure">
                        {duplicate.shortId}
                      </Link>
                      <span style={{ marginLeft: "8px" }}>
                        Match score: {duplicate.matchScore}
                      </span>
                    </div>
                  ))}
                </div>
              }
            />
          )}
          <Styled.InfoItem
            label="EWA Status"
            value={
              <Styled.TagWrapper>
                <OnboardingStateWithProgress
                  user={user}
                  productName={ProductName.earnedWageAccess}
                />
              </Styled.TagWrapper>
            }
          />
          <Styled.InfoItem
            label="Tax Refund Advance Status"
            value={
              <Styled.TagWrapper>
                <OnboardingStateWithProgress
                  user={user}
                  productName={ProductName.taxRefundAdvance}
                />
              </Styled.TagWrapper>
            }
          />
          <Styled.InfoItem
            label="Account Status"
            value={
              <Styled.TagWrapper>
                <StatusTag
                  status={user.deactivated ? "deactivated" : "active"}
                />
              </Styled.TagWrapper>
            }
          />
          {partnerSlug && (
            <Styled.InfoItem
              label="Partner"
              value={
                <Styled.TagWrapper>
                  <PartnerTag slug={partnerSlug} />
                </Styled.TagWrapper>
              }
            />
          )}
          {referralSlug && referralSlug !== partnerSlug && (
            <Styled.InfoItem
              label="Referral Partner"
              value={
                <Styled.TagWrapper>
                  <PartnerTag slug={referralSlug} />
                </Styled.TagWrapper>
              }
            />
          )}

          {user.acquisitionMetadata?.productEntryPoint && (
            <Styled.InfoItem
              label="Product Entry Point"
              value={user.acquisitionMetadata.productEntryPoint}
            />
          )}
          {user.acquisitionMetadata?.utmSource && (
            <Styled.InfoItem
              label="UTM Source"
              value={user.acquisitionMetadata.utmSource}
            />
          )}
          {user.acquisitionMetadata?.utmMedium && (
            <Styled.InfoItem
              label="UTM Medium"
              value={user.acquisitionMetadata.utmMedium}
            />
          )}
          {user.acquisitionMetadata?.utmCampaign && (
            <Styled.InfoItem
              label="UTM Campaign"
              value={user.acquisitionMetadata.utmCampaign}
            />
          )}
          {user.acquisitionMetadata?.groupId && (
            <Styled.InfoItem
              label="Group ID"
              value={user.acquisitionMetadata.groupId}
            />
          )}

          <Styled.InfoItem
            label="Bank"
            value={user.linkedBankAccountName || "n/a"}
          />
          <Styled.InfoItem
            label="Payroll Provider"
            value={user.payrollAccount?.institutionName || "n/a"}
          />
        </Styled.InfoSection>
      </Styled.Info>
    </Styled.SectionTile>
  );
};

export default BasicInfo;
